import { useLocation } from "react-router";

import { teleopLoader } from "app/(new)/o/[orgId]/(feature-flagged)/(real-time-ops)/_utils/queryStrings";

/**
 * Utility function to determine if the current window is inside an iframe.
 */
export const getIsInIframe = () => {
  return typeof window !== "undefined" && window.self !== window.top;
};

const getTeleopQueryParams = (search?: string) =>
  teleopLoader(search ?? (typeof window !== "undefined" ? window.location.search : ""));

export const getIsInDfrCommand = () => {
  const isInIframe = getIsInIframe();
  if (!isInIframe) {
    return false;
  }

  return getTeleopQueryParams().dfr === true;
};

export const getIsInRtic = () => {
  const isInIframe = getIsInIframe();
  if (!isInIframe) {
    return false;
  }

  return getTeleopQueryParams().rtic === true;
};

export const getIsViewOnlyRfd = () => {
  const isInIframe = getIsInIframe();
  if (!isInIframe) {
    return false;
  }

  return getTeleopQueryParams().viewOnly === true;
};

type HiddenFromIframeProps = {
  children: React.ReactNode;
};

/**
 * HOC that hides its children if the current window is inside an iframe.
 * Primarily used for conditionally rendering portions of RFD when embedding
 * in DFR Command through an iframe.
 */
export const HiddenFromIframe = ({ children }: HiddenFromIframeProps) => {
  const isInIframe = getIsInIframe();

  if (isInIframe) {
    return null;
  }

  return children;
};

export const HiddenFromDfrCommand = ({ children }: HiddenFromIframeProps) => {
  const isInIframe = getIsInIframe();
  const { search } = useLocation();
  const isDfrCommand = getTeleopQueryParams(search).dfr === true;

  if (isInIframe && isDfrCommand) {
    return null;
  }

  return children;
};

export const HiddenFromRtic = ({ children }: HiddenFromIframeProps) => {
  const isInIframe = getIsInIframe();
  const { search } = useLocation();
  const isRtic = getTeleopQueryParams(search).rtic === true;

  if (isInIframe && isRtic) {
    return null;
  }

  return children;
};

export const HiddenFromViewOnlyRfd = ({ children }: HiddenFromIframeProps) => {
  const isInIframe = getIsInIframe();
  const { search } = useLocation();
  const { viewOnly, rtic } = getTeleopQueryParams(search);

  if (isInIframe && (viewOnly === true || rtic === true)) {
    return null;
  }

  return children;
};
